// src/components/Store/ItemStore.jsx
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Stack,
  TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../../api/axios';
import SafeChip from '../Common/SafeChip';
import { useUser } from '../../contexts/UserContext';

const getFullImageUrl = (url) =>
  url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const ITEMS_PER_PAGE = 5;

const ItemStore = () => {
  const { userInfo, refreshUserInfo } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchItems = () => {
    api.get('/api/rewards/items/')
      .then(res => setItems(res.data.items || []))
      .catch(() => alert('아이템 목록을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  };

  const handlePurchase = async (id, price) => {
    if (userInfo.points < price) {
      alert('Leaf🌿가 부족합니다!');
      return;
    }
    if (!window.confirm('구매하시겠습니까?')) return;

    try {
      await api.post('/api/rewards/items/purchase/', { item_id: id });
      await refreshUserInfo();
      fetchItems();
      alert('구매 완료!');
    } catch {
      alert('구매 실패');
    }
  };

  useEffect(fetchItems, []);

  if (loading) {
    return <CircularProgress sx={{ mt: 4 }} />;
  }

  const filtered = items.filter(item => item.name.includes(search));
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedItems = isMobile
    ? filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
    : filtered;

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box p={2}>
      {!isMobile && (
        <Typography variant="h5" gutterBottom>
          🎫 쿠폰 상점
        </Typography>
      )}

      {isMobile && (
        <TextField
          fullWidth
          size="small"
          placeholder="아이템 검색"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      {isMobile ? (
        <>
          {paginatedItems.map(item => {
            const isSoldOut = !item.is_unlimited && item.remaining <= 0;
            return (
              <Accordion key={item.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" flexDirection="column" width="100%">
                    <Typography fontWeight="bold">{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.price} Leaf🌿
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {item.image && (
                    <Box mb={1} textAlign="center">
                      <img
                        src={getFullImageUrl(item.image)}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
                  >
                    {item.description || '설명 없음'}
                  </Typography>

                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                    {item.is_coupon && <SafeChip label="쿠폰" size="small" />}
                    {item.is_unlimited ? (
                      <SafeChip label="무제한" size="small" />
                    ) : isSoldOut ? (
                      <SafeChip label="매진" size="small" />
                    ) : (
                      <SafeChip label={`남은: ${item.remaining}`} size="small" />
                    )}
                    <SafeChip label={`${item.price}P`} size="small" />
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="medium"
                    disabled={isSoldOut || userInfo.points < item.price}
                    onClick={() => handlePurchase(item.id, item.price)}
                  >
                    {isSoldOut
                      ? '품절'
                      : userInfo.points < item.price
                        ? '부족'
                        : '구매하기'}
                  </Button>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {totalPages > 1 && (
            <Stack mt={2} alignItems="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Stack>
          )}
        </>
      ) : (
        <Grid container spacing={2}>
          {paginatedItems.map(item => {
            const isSoldOut = !item.is_unlimited && item.remaining <= 0;

            return (
              <Grid item xs={6} sm={4} md={3} key={item.id}>
                <Card>
                  {item.image ? (
                    <CardMedia
                      component="img"
                      image={getFullImageUrl(item.image)}
                      alt={item.name}
                      title={item.name}
                      aria-hidden="true"
                      height="100"
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box height={100} />
                  )}

                  <CardContent sx={{ padding: '8px 16px 24px' }}>
                    <Typography align="center" noWrap>
                      {item.name}
                    </Typography>
                    <Typography
                      align="center"
                      variant="body2"
                      color="text.secondary"
                      noWrap
                    >
                      {item.description || '설명 없음'}
                    </Typography>

                    <Box
                      mt={1}
                      display="flex"
                      justifyContent="center"
                      flexWrap="wrap"
                      gap={0.5}
                    >
                      {item.is_coupon && <SafeChip label="쿠폰" size="small" />}
                      {item.is_unlimited ? (
                        <SafeChip label="무제한" size="small" />
                      ) : isSoldOut ? (
                        <SafeChip label="매진" size="small" />
                      ) : (
                        <SafeChip label={`남은: ${item.remaining}`} size="small" />
                      )}
                      <SafeChip label={`${item.price} 🌿`} size="small" />
                    </Box>

                    <Box mt={1} display="flex" justifyContent="center">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSoldOut || userInfo.points < item.price}
                        onClick={() => handlePurchase(item.id, item.price)}
                      >
                        {isSoldOut
                          ? '품절'
                          : userInfo.points < item.price
                            ? '부족'
                            : '구매하기'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ItemStore;